import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Bookmark, Trash2, Calendar } from "lucide-react";
import { getBookmarks, removeBookmark } from "@/lib/bookmarks";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function BookmarksPage() {
  const [bookmarks, setBookmarks] = useState([]);

  useEffect(() => {
    setBookmarks(getBookmarks());
  }, []);

  const handleRemove = (slug) => {
    const updated = removeBookmark(slug);
    setBookmarks(updated);
    toast("Article retiré des signets");
  };

  return (
    <>
      <Helmet><title>Mes signets - OFALycée</title></Helmet>
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-16" data-testid="bookmarks-page">
        <div className="flex items-center gap-3 mb-2">
          <Bookmark className="h-6 w-6 text-primary" />
          <h1 className="font-heading text-3xl md:text-4xl">Mes signets</h1>
        </div>
        <p className="text-muted-foreground mb-10">
          Articles sauvegardés localement dans votre navigateur.
        </p>

        {bookmarks.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-border rounded-lg" data-testid="bookmarks-empty">
            <Bookmark className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground mb-4">Aucun article sauvegardé pour le moment.</p>
            <Link to="/blog">
              <Button variant="outline" className="rounded-none">Explorer les articles</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {bookmarks.map((b) => (
              <div key={b.slug} className="flex gap-4 p-4 border border-border rounded-lg hover:border-primary/30 transition-colors group" data-testid={`bookmark-${b.slug}`}>
                {b.featured_image && (
                  <Link to={`/article/${b.slug}`} className="flex-shrink-0">
                    <img src={b.featured_image} alt={b.title} className="w-24 h-16 object-cover rounded" />
                  </Link>
                )}
                <div className="flex-1 min-w-0">
                  <Link to={`/article/${b.slug}`} className="hover:text-primary transition-colors">
                    <h3 className="font-medium text-foreground line-clamp-1 mb-1">{b.title}</h3>
                  </Link>
                  {b.category && <span className="text-xs text-primary uppercase tracking-wider">{b.category}</span>}
                  {b.saved_at && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                      <Calendar className="h-3 w-3" />
                      Sauvegardé le {new Date(b.saved_at).toLocaleDateString("fr-FR")}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => handleRemove(b.slug)}
                  className="p-2 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100"
                  title="Supprimer"
                  data-testid={`remove-bookmark-${b.slug}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
